/**
 * "Skip this welcome screen next time" — the preference, and where it sends you.
 *
 * Two keys rather than one, because they answer different questions: whether to skip at
 * all, and what to open instead. Kept here rather than inline in the start page because
 * the page WRITES them and the module ROUTER reads them, and a second copy of a
 * localStorage key name is how the two quietly stop agreeing.
 *
 * NOTE — before this file existed, `apr.skipStart` was written by the toggle and read by
 * nothing at all. The switch saved a preference the app never consulted, so the welcome
 * screen appeared every time regardless. `shouldSkipStart` is what finally honours it.
 */

const SKIP_KEY = 'apr.skipStart';
const TARGET_KEY = 'apr.skipStartTarget';

/**
 * Where skipping can land you: the four top-level sections of APR's nav.
 *
 * The VALUE is the bucket, but skipping never stops on the bucket itself — see
 * `shouldSkipStart`. A bucket's own path renders a list of cards, and arriving on a menu
 * of links is not skipping to a screen, it is one more screen to click through. The
 * bucket is stored because it is what the user picked and what the label says; the
 * screen is resolved from it at the moment of navigation.
 */
export const SKIP_TARGETS = [
  { path: '/apr/portfolio', label: 'Portfolio' },
  { path: '/apr/knowledge-library', label: 'Knowledge Library' },
  { path: '/apr/license-intelligence', label: 'License Intelligence' },
  { path: '/apr/application-evaluation', label: 'Application Evaluation' },
] as const;

export const DEFAULT_SKIP_TARGET = SKIP_TARGETS[0].path;

export function isSkipOn(): boolean {
  return localStorage.getItem(SKIP_KEY) === '1';
}

export function getSkipTarget(): string {
  const stored = localStorage.getItem(TARGET_KEY);
  // Only a target still on the list is honoured. A path saved before the list changed
  // would otherwise send the user somewhere that no longer exists.
  return SKIP_TARGETS.some((t) => t.path === stored) ? stored! : DEFAULT_SKIP_TARGET;
}

export function setSkip(on: boolean): void {
  if (on) localStorage.setItem(SKIP_KEY, '1');
  else localStorage.removeItem(SKIP_KEY);
}

export function setSkipTarget(path: string): void {
  localStorage.setItem(TARGET_KEY, path);
}

/** Clears both keys — the welcome screen comes back on the next sign-in. */
export function resetSkip(): void {
  localStorage.removeItem(SKIP_KEY);
  localStorage.removeItem(TARGET_KEY);
}

/** The shape this needs from the catalogue — a section that may hold sections. */
export interface NavSection {
  path: string;
  sections?: readonly NavSection[] | undefined;
}

/**
 * The first actual SCREEN inside a section, walking down through nested buckets.
 *
 * Application Evaluation's first child is itself a bucket (APR Model & Execution), so
 * this recurses rather than taking the first child: one level down would land on
 * another list of cards.
 */
export function firstScreenOf(section: NavSection): string {
  const child = section.sections?.[0];
  return child ? firstScreenOf(child) : section.path;
}

/**
 * The path to redirect to, or null to show the welcome screen.
 *
 * Resolves the stored BUCKET to the first screen inside it, because skipping should land
 * on something you can work in, not on a menu. `sections` is this user's own nav tree, so
 * a bucket they can no longer open simply is not found and the welcome screen shows
 * instead of bouncing them to a route the server would refuse.
 */
/**
 * The screen a stored bucket resolves to, for callers that already know the bucket.
 *
 * The start screen's own "Open X now" button needs this: it used to navigate to the
 * stored bucket path directly, which dropped the user on that bucket's list of cards —
 * the very thing skipping is meant to avoid. Same resolution as `shouldSkipStart`, so
 * the button and the automatic redirect cannot disagree about where "Knowledge Library"
 * means.
 */
export function resolveSkipPath(
  sections: readonly NavSection[],
  bucketPath: string,
): string {
  const bucket = sections.find((s) => s.path === bucketPath);
  return bucket ? firstScreenOf(bucket) : bucketPath;
}

export function shouldSkipStart(
  sections: readonly NavSection[],
  /** The current query string. `?start=1` means the user explicitly asked for the
   *  welcome screen, so the preference is bypassed for that navigation only. Without
   *  this, turning the preference on hides the only control that turns it off. */
  search = '',
): string | null {
  if (new URLSearchParams(search).get('start') === '1') return null;
  if (!isSkipOn()) return null;
  const target = getSkipTarget();
  const bucket = sections.find((s) => s.path === target);
  return bucket ? firstScreenOf(bucket) : null;
}
